import { localizedStrings } from '@core/strings'
import React, { useEffect, useState } from 'react'
import { Text } from '@fundamentals'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  TextInput,
  MenuItem,
  LoadingButton,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { FormProvider, useForm } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import { RegisterTemplate } from '@core/entities/register/RegisterTemplate'
import { Register } from '@core/entities/register/Register'
import {
  RegisterItem,
  RegisterItemFlatPropertyData,
} from '@core/entities/register/RegisterItem'
import {
  useCreateRegisterItem,
  useGetRegisterItem,
  useUpdateRegisterItem,
} from '@core/react-query/features/registers/registers/register/items/item'
import { ModalDialogContainer } from '@common/modal/ModalDialogContainer'
import { useGetRegisterViews } from '@core/react-query/features/registers/registers/register/hooks/useGetRegisterViews'
import { useGetRegisterTemplateView } from '@core/react-query/features/registers/views'
import { useToast } from '@core/toast'
import { RegisterItemPropertiesFormTabs } from '../items/form/RegisterItemPropertiesFormTabs'

import { RegisterFilterScopes } from '../types'
import { PageUrls } from '@core/page-urls'
import { useRouter } from 'next/router'
import { RegisterAddDiagramModal } from '@modules/registers/details/RegisterAddDiagramModal'

interface CreateEditRegisterItemModalProps {
  scope: RegisterFilterScopes
  registerViewId?: number
  registerTemplate: RegisterTemplate
  registerItem?: RegisterItem
  register: Register
  focusProperty?: RegisterItemFlatPropertyData
  propertyId?: number
}
type FormSchema = {
  updateNote?: string
  [x: string]: string
}

export const CreateEditRegisterItemModal: React.FC<
  CreateEditRegisterItemModalProps
> = ({
  scope,
  registerViewId,
  registerTemplate,
  registerItem,
  register,
  focusProperty,
  propertyId,
}) => {
  const { showModal, hideModal } = useModal()
  const router = useRouter()
  const toast = useToast()
  const createRegisterItem = useCreateRegisterItem()
  const updateRegisterItem = useUpdateRegisterItem()
  const registerId = register.id

  const [view, setView] = useState<number>(registerViewId)

  const { data: registerItemData, isFetching } = useGetRegisterItem(
    {
      registerId,
      registerItemId: registerItem?.id,
    },
    { enabled: !!registerItem, cacheTime: 0 },
  )

  const { data: registerView } = useGetRegisterTemplateView(
    {
      templateId: registerTemplate?.id,
      viewId: view,
    },
    {
      enabled: !!view && !!registerTemplate?.id,
    },
  )

  const { data: registerViews } = useGetRegisterViews(
    {
      registerId: registerId,
    },
    {
      enabled: !!registerId,
    },
  )

  const registerProperties = registerTemplate.registerProperties

  const registerPropertiesInView = registerProperties.filter((property) =>
    registerView?.properties
      ? registerView?.properties
          .map((p) => Number(p.propertyId.split('.')[0]))
          .includes(property.id)
      : true,
  )

  const defaultValues = registerTemplate.getDefaultFormValues(
    registerItem ? registerItem?.dataWithLinkProperties : [],
  )

  const getDefaultUniqueIdValue = (
    registerItem: RegisterItem,
    registerTemplate: RegisterTemplate,
  ) => {
    if (registerItem) {
      return registerItem.uniqueId
    } else if (!registerTemplate?.uniqueIdSystemControlled) {
      return uuidv4().substring(0, 8).toUpperCase()
    }
    return null
  }

  const formContext = useForm<FormSchema>({
    defaultValues: {
      ...defaultValues,
      uniqueId: getDefaultUniqueIdValue(registerItem, registerTemplate),
    },
  })

  const onSubmit = (data: FormSchema) => {
    const formData = registerTemplate.getSubmitData(data)

    if (registerItem) {
      updateRegisterItem.mutate(
        {
          registerId,
          registerItemId: registerItem.id,
          data: formData,
          updateNote: data.updateNote,
        },
        {
          onSuccess: () => {
            toast.success(localizedStrings.registerItemUpdated)
            hideModal()
          },
          onError: (e) => toast.error(e?.message),
        },
      )
    } else {
      createRegisterItem.mutate(
        {
          uniqueId: data.uniqueId,
          registerId,
          data: formData,
        },
        {
          onSuccess: () => {
            toast.success(localizedStrings.registerItemCreated)
            hideModal()
          },
          onError: (e) => toast.error(e?.message),
        },
      )
    }
  }

  const uniqueId = formContext.watch('uniqueId')

  useEffect(() => {
    if (registerItemData) {
      // We maintain the values for dirty fields
      // If the user changes a value and then they go to the calculated by formula tab
      // After they override a formula, the dirty input value is kept and not overwritten
      const dirtyFields = Object.keys(formContext.formState.dirtyFields)
      const dirtyValues = dirtyFields.reduce((acc, key) => {
        acc[key] = formContext.getValues(key)
        return acc
      }, {})

      formContext.reset(
        {
          uniqueId,
          ...registerTemplate.getDefaultFormValues(
            registerItemData.dataWithLinkProperties,
          ),
          ...dirtyValues,
        },
        { keepDirty: true, keepDirtyValues: true },
      )
    }
  }, [registerItem, isFetching])

  if (
    !register?.diagrams?.length &&
    register.canEdit() &&
    registerTemplate.isDiagramEnabled() &&
    scope.type === 'PROJECT'
  ) {
    return (
      <ModalDialogContainer>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20, mb: 0 }}
        >
          {localizedStrings.addItem}
          <IconButton aria-label='close-modal' onClick={hideModal}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Text pt={2}>
            {localizedStrings.uploadADiagramToStartCreatingNewItems}
          </Text>
        </DialogContent>

        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button variant='text' onClick={hideModal}>
            {localizedStrings.cancel}
          </Button>
          <Button
            variant='text'
            data-test='add-diagram-button'
            onClick={() =>
              showModal(
                <RegisterAddDiagramModal
                  organisationId={scope.organisationId}
                  projectId={register?.projectId}
                  registerId={register?.id}
                  onSuccess={hideModal}
                />,
              )
            }
          >
            {localizedStrings.addDiagram}
          </Button>
        </DialogActions>
      </ModalDialogContainer>
    )
  }

  if (registerTemplate.isDiagramEnabled() && !registerItem) {
    return (
      <ModalDialogContainer>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20, mb: 0 }}
        >
          {localizedStrings.addItem}
          <IconButton aria-label='close-modal' onClick={hideModal}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Text pt={2}>
            {
              localizedStrings.forThisRegisterNewItemsNeedToBeCreatedInTheDiagramView
            }
          </Text>
        </DialogContent>

        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button variant='text' onClick={hideModal}>
            {localizedStrings.cancel}
          </Button>
          <Button
            variant='text'
            data-test='switch-to-diagram-view-button'
            onClick={async () => {
              const {
                registerId: regId,
                projectId,
                id,
                ...query
              } = router.query

              // Land on the register's first diagram, the same way the
              // table/diagram toggle in the header does — without a documentId
              // the diagram view has nothing to open.
              const commonArgs = {
                organisationId: scope.organisationId,
                registerId,
                query: {
                  ...query,
                  documentId: register?.diagrams?.[0]?.siteBoxDocumentId,
                },
              }

              await router.push(
                scope.type === 'PROJECT'
                  ? PageUrls.projectRegisterDetailsDiagram({
                      ...commonArgs,
                      projectId: scope.projectId,
                    })
                  : PageUrls.organisationRegisterDetailsDiagram(commonArgs),
              )

              hideModal()
            }}
          >
            {localizedStrings.switchToDiagramView}
          </Button>
        </DialogActions>
      </ModalDialogContainer>
    )
  }

  return (
    <ModalDialogContainer preventDialogClose maxWidth='md'>
      <FormProvider {...formContext}>
        <form
          style={{
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          onSubmit={formContext.handleSubmit(onSubmit)}
        >
          <DialogTitle
            variant='h5'
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            sx={{ pr: 1, fontSize: 20, mb: 0 }}
          >
            {registerItem
              ? localizedStrings.updateRegisterItem
              : localizedStrings.addRegisterItem}
            <IconButton aria-label='close-modal' onClick={hideModal}>
              <ClearIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              pt: 2,
            }}
          >
            {!!registerViews?.views?.length && !propertyId && (
              <TextInput
                label={localizedStrings.selectView}
                select
                value={registerView ? registerView.id : 'default'}
                onChange={(e) => setView(Number(e.target.value))}
                sx={{ mt: 3 }}
              >
                <MenuItem value='default'>
                  {localizedStrings.defaultView}
                </MenuItem>
                {registerViews.views.map((view) => {
                  return (
                    <MenuItem key={view.id} value={view.id}>
                      {view.name}
                    </MenuItem>
                  )
                })}
              </TextInput>
            )}
            <RegisterItemPropertiesFormTabs
              register={register}
              registerItem={registerItemData}
              registerTemplate={registerTemplate}
              registerProperties={registerPropertiesInView}
              focusProperty={focusProperty}
              propertyId={propertyId}
            />
          </DialogContent>

          <DialogActions sx={{ pb: 2, pr: 3 }}>
            <Button variant='outlined' onClick={hideModal}>
              {localizedStrings.cancel}
            </Button>
            <LoadingButton
              variant='contained'
              type='submit'
              data-test='submit-register-item'
              loading={
                createRegisterItem.isLoading || updateRegisterItem.isLoading
              }
            >
              {localizedStrings.submit}
            </LoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </ModalDialogContainer>
  )
}
